import { DIRECTIONS, dx, dy, type Direction, type Turn } from '../../sim/direction';
import type { SimLevel } from '../../sim/level';
import type { MowerSpec } from '../../sim/mower';
import { Simulation } from '../../sim/simulation';
import { GreedyPlanner } from './greedyPlanner';
import { motionModelFor, type Action, type MotionModel } from './motionModel';
import { NARROW_BODY, StateSpace } from './stateSpace';

export interface SoccerResult {
  won: boolean;
  ticks: number;
  crashes: number;
  goals: number;
  /** The tick of each goal. */
  goalTicks: number[];
  /** Tile centers reached, with the fuel each step burned (for placing gas cans). */
  route: { x: number; y: number; fuel: number }[];
}

/**
 * Plays mower soccer: works out the ball's way to the nearer goal mouth one kick at a time (only
 * kicks that leave room to drive in behind it), drives round to kick it along that way, and
 * replans after every kick. A ball it cannot get behind (on a fence) is kicked from any side, so
 * it squirts loose. Drives the soccer bot and the in-game autopilot on soccer lawns.
 */
export class SoccerPilot {
  private readonly model: MotionModel;
  private readonly space: StateSpace;
  private mower: GreedyPlanner | null = null;

  /**
   * `goals`: the goal mouth tiles to kick into (a soccer match: the end this seat attacks); every
   * mouth when absent.
   */
  constructor(
    private readonly sim: Simulation,
    private readonly goals?: readonly { x: number; y: number }[],
  ) {
    this.model = motionModelFor(sim.mowerSpec);
    this.space = new StateSpace(sim.grid, this.model, NARROW_BODY(sim.mowerSpec.cutPower));
  }

  /** At a tile center: the turn to ask for (null to keep going straight). */
  decide(): Turn | null {
    const sim = this.sim;
    if (sim.mower.progress !== 0) return null;
    const ball = sim.pitchBall;
    if (ball && sim.level.soccer) {
      const rest = this.restingSpot(ball);
      const way = this.ballWay(rest.x, rest.y);
      const tries = way
        ? [way.dir]
        : DIRECTIONS.filter((d) => this.open(rest.x - dx(d), rest.y - dy(d)));
      for (const dir of tries) {
        const action = this.pushPath(rest.x, rest.y, dir)?.[0];
        if (action) return action === 'straight' ? null : action;
      }
    }
    // Nowhere to kick from right now: keep mowing meanwhile.
    this.mower ??= new GreedyPlanner(sim);
    const action = this.mower.nextAction();
    return !action || action === 'straight' ? null : action;
  }

  private open(x: number, y: number): boolean {
    return (
      this.sim.grid.inBounds(x, y) && !this.sim.grid.isBlockedFor(x, y, this.sim.mowerSpec.cutPower)
    );
  }

  /** Where a rolling ball will come to rest (as far as the lawn goes; the sim has the last word). */
  private restingSpot(b: { x: number; y: number; dir: Direction; left: number }): {
    x: number;
    y: number;
  } {
    let { x, y } = b;
    for (let k = 0; k < b.left && this.open(x + dx(b.dir), y + dy(b.dir)); k++) {
      x += dx(b.dir);
      y += dy(b.dir);
    }
    return { x, y };
  }

  /**
   * The ball's shortest way into a goal mouth in kicks of one tile: each needs room ahead for the
   * ball and room behind it for the mower. Returns the first kick's direction and the length.
   */
  private ballWay(bx: number, by: number): { dir: Direction; length: number } | null {
    const w = this.sim.grid.width;
    const mouths = new Set((this.goals ?? this.sim.level.soccer!.mouths).map((p) => p.y * w + p.x));
    const start = by * w + bx;
    const first = new Map<number, Direction>();
    const dist = new Map<number, number>([[start, 0]]);
    const queue = [{ x: bx, y: by }];
    for (let head = 0; head < queue.length; head++) {
      const p = queue[head]!;
      const here = p.y * w + p.x;
      if (here !== start && mouths.has(here)) {
        return { dir: first.get(here)!, length: dist.get(here)! };
      }
      for (const d of DIRECTIONS) {
        const nx = p.x + dx(d);
        const ny = p.y + dy(d);
        const n = ny * w + nx;
        if (dist.has(n) || !this.open(nx, ny)) continue;
        if (!this.open(p.x - dx(d), p.y - dy(d))) continue; // room for the mower behind
        dist.set(n, dist.get(here)! + 1);
        first.set(n, here === start ? d : first.get(here)!);
        queue.push({ x: nx, y: ny });
      }
    }
    return null;
  }

  /**
   * Actions from the mower's state to a kick: driving straight into the ball at (bx, by) heading
   * `dir`. Never through the ball any other way.
   */
  private pushPath(bx: number, by: number, dir: Direction): Action[] | null {
    const { space, model, sim } = this;
    const m = sim.mower;
    const start = space.index(m.x, m.y, m.dir, model.fromMower(m));
    const prev = new Map<number, { from: number; action: Action }>();
    const seen = new Set<number>([start]);
    const queue = [start];
    for (let head = 0; head < queue.length; head++) {
      const i = queue[head]!;
      const s = space.decode(i);
      if (i !== start && s.x === bx && s.y === by && s.dir === dir) {
        const actions: Action[] = [];
        for (let k = i; k !== start; k = prev.get(k)!.from) actions.unshift(prev.get(k)!.action);
        return actions;
      }
      space.forEachSuccessor(i, (j, action) => {
        if (seen.has(j)) return;
        const t = space.decode(j);
        if (t.x === bx && t.y === by) {
          const isKick = t.dir === dir && action === 'straight';
          if (!isKick) return;
        }
        seen.add(j);
        prev.set(j, { from: i, action });
        queue.push(j);
      });
    }
    return null;
  }
}

/**
 * Plays a soccer lawn with the {@link SoccerPilot} until the whistle (or, with `stopAtGoals`, until
 * that many goals: for timing the half).
 */
export function solveSoccer(
  level: SimLevel,
  mower: MowerSpec,
  {
    maxTicks = 200_000,
    ignoreFuel = false,
    stopAtGoals,
  }: { maxTicks?: number; ignoreFuel?: boolean; stopAtGoals?: number } = {},
): SoccerResult {
  const spec: MowerSpec =
    ignoreFuel && mower.fuelTiles !== undefined ? { ...mower, fuelTiles: 1_000_000_000 } : mower;
  const sim = new Simulation(level, spec);
  sim.drainEvents();
  const pilot = new SoccerPilot(sim);
  let crashes = 0;
  const goalTicks: number[] = [];
  const route = [{ x: sim.mower.x, y: sim.mower.y, fuel: 0 }];
  let fuel = sim.mower.fuel;
  while (sim.status === 'running' && sim.tick < maxTicks) {
    if (stopAtGoals !== undefined && sim.goals >= stopAtGoals) break;
    const turn = pilot.decide();
    if (turn) sim.enqueue({ type: 'turn', turn });
    for (const e of sim.step()) {
      if (e.type === 'crashed') crashes++;
      if (e.type === 'goal') goalTicks.push(e.tick);
    }
    const now = sim.mower;
    const last = route[route.length - 1];
    if (now.progress === 0 && (last?.x !== now.x || last.y !== now.y)) {
      route.push({
        x: now.x,
        y: now.y,
        fuel: Number.isFinite(now.fuel) && now.fuel < fuel ? fuel - now.fuel : 1,
      });
    }
    fuel = now.fuel;
  }
  return {
    won: sim.status === 'won',
    ticks: sim.tick,
    crashes,
    goals: sim.goals,
    goalTicks,
    route,
  };
}
